import React, { useState, useEffect } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { startEllipsisInterval } from "../utils/common";
import styles from "./FlausFlosserForm.module.css";

const GOOGLE_LIBRARIES = ["places"];

const FlausFlosserForm = ({ initialData, businessName, businessLogo }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState(initialData?.firstName || "");
  const [lastName, setLastName] = useState(initialData?.lastName || "");
  const [email, setEmail] = useState(initialData?.email || "");
  const [orderId, setOrderId] = useState(initialData?.orderId || "");
  const [fromAddressDisplay, setFromAddressDisplay] = useState(
    initialData?.address?.address1
      ? `${initialData.address.address1}${initialData.address.address2 ? " " + initialData.address.address2 : ""}, ${initialData.address.city}, ${initialData.address.province} ${initialData.address.zip}`
      : ""
  );
  const [fromAddressStructured, setFromAddressStructured] = useState(
    initialData?.address?.address1 ? initialData.address : null
  );
  const [businessAddress, setBusinessAddress] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadText, setDownloadText] = useState("Downloading");
  const [autocompleteInstance, setAutocompleteInstance] = useState(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_LIBRARIES,
  });

  useEffect(() => {
    const fetchBusinessAddress = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/get_business_address_portal/?business=${encodeURIComponent(businessName)}`);
        const data = await res.json();
        setBusinessAddress(data.address || "");
      } catch (err) {
        console.log("Error fetching business address:", err);
      }
    };
    fetchBusinessAddress();
  }, [apiUrl, businessName]);

  useEffect(() => {
    if (!isDownloading) return;
    const interval = startEllipsisInterval(setDownloadText, "Downloading");
    return () => clearInterval(interval);
  }, [isDownloading]);

  const isFormComplete =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    orderId.trim() &&
    fromAddressStructured?.address1;

  const parseAndSetAddress = (place) => {
    if (!place || !place.address_components) return;
    const get = (type) =>
      place.address_components.find((c) => c.types.includes(type))?.long_name || "";
    const getShort = (type) =>
      place.address_components.find((c) => c.types.includes(type))?.short_name || "";

    const streetNumber = get("street_number");
    const route = get("route");
    const city =
      get("locality") ||
      get("sublocality_level_1") ||
      get("neighborhood") ||
      get("administrative_area_level_2");
    const state = getShort("administrative_area_level_1");
    const zip = get("postal_code");
    const country = getShort("country") || "US";

    setFromAddressDisplay(place.formatted_address || "");
    setFromAddressStructured({
      address1: `${streetNumber} ${route}`.trim(),
      address2: "",
      city,
      province: state,
      zip,
      country,
    });
  };

  const handleDownload = async () => {
    if (!isFormComplete || isDownloading) return;
    setIsDownloading(true);

    const sessionId =
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const createRes = await fetch(`${apiUrl}/api/create_flaus_return_link/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: orderId.trim(),
          business: businessName,
          customer: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            address: fromAddressStructured,
          },
        }),
      });
      if (!createRes.ok) throw new Error("Failed to create return");
      const { token } = await createRes.json();

      await fetch(`${apiUrl}/api/portal_session/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, session_id: sessionId }),
      });

      const shipmentRes = await fetch(`${apiUrl}/api/create_shipment/${token}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Portal-Session": sessionId,
        },
      });
      if (!shipmentRes.ok) throw new Error("Failed to create shipment");
      const shipmentData = await shipmentRes.json();
      const rate = parseFloat(shipmentData.selected_rate?.rate || 0);

      const labelRes = await fetch(`${apiUrl}/api/purchase_shipping_label/${token}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Portal-Session": sessionId,
        },
        body: JSON.stringify({ label_purchase_price: rate }),
      });
      if (!labelRes.ok) throw new Error("Failed to purchase label");
      const labelData = await labelRes.json();

      window.open(labelData.label_url, "_blank");
      navigate(`/returns_confirm/${orderId.trim()}?token=${token}`);
    } catch (err) {
      console.error("Flaus label error:", err);
      alert("There was an error downloading your label. Please check your address and try again.");
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header — logo same size as initial portal render */}
      <div className={styles.header}>
        {businessLogo && (
          <img
            src={`${process.env.PUBLIC_URL}/business_images/${businessLogo}`}
            alt="Flaus"
            className={styles.logo}
          />
        )}
        <img
          src={`${process.env.PUBLIC_URL}/images/flosser_box.png`}
          alt=""
          className={styles.header_flosser}
          aria-hidden="true"
        />
      </div>
      <p className={styles.subtitle}>Recycle your used Flaus Heads.</p>

      {/* Form card */}
      <div className={styles.form_card}>
        <div className={styles.name_row}>
          <div className={styles.field_group}>
            <label className={styles.label}>First name</label>
            <input className={styles.input} placeholder="Jane" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className={styles.field_group}>
            <label className={styles.label}>Last name</label>
            <input className={styles.input} placeholder="Smith" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>

        <div className={styles.field_group}>
          <label className={styles.label}>Email</label>
          <input className={styles.input} type="email" placeholder="jane@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className={styles.field_group}>
          <label className={styles.label}>Order #</label>
          <input className={styles.input} placeholder="e.g. 10042" value={orderId} onChange={(e) => setOrderId(e.target.value)} />
        </div>

        <div className={styles.field_group}>
          <label className={styles.label}>Send from</label>
          {isLoaded ? (
            <Autocomplete
              onLoad={(ac) => setAutocompleteInstance(ac)}
              onPlaceChanged={() => {
                if (autocompleteInstance) parseAndSetAddress(autocompleteInstance.getPlace());
              }}
              options={{ types: ["address"], componentRestrictions: { country: "us" } }}
            >
              <input
                className={styles.input}
                placeholder="Start typing your address..."
                value={fromAddressDisplay}
                onChange={(e) => {
                  setFromAddressDisplay(e.target.value);
                  if (fromAddressStructured) setFromAddressStructured(null);
                }}
              />
            </Autocomplete>
          ) : (
            <input className={styles.input} placeholder="Start typing your address..." disabled />
          )}
        </div>

        <div className={styles.field_group}>
          <label className={styles.label}>Send to</label>
          <input className={`${styles.input} ${styles.input_locked}`} value={businessAddress} readOnly />
          <p className={styles.locked_note}>Flaus returns warehouse — cannot be changed</p>
        </div>

        <div className={styles.divider} />

        {/* Packing instructions */}
        <div className={styles.packing_section}>
          <p className={styles.packing_title}>Packing instructions</p>
          <div className={styles.packing_diagram}>
            {/* Flosser box icon */}
            <img
              src={`${process.env.PUBLIC_URL}/images/flosser_box.png`}
              alt="Flosser box"
              className={styles.flosser_box_img}
            />
            <span className={styles.packing_arrow}>&#8594;</span>
            {/* Bubble mailer with dimensions */}
            <div className={styles.mailer_wrapper}>
              <img
                src={`${process.env.PUBLIC_URL}/images/flaus_bubble_mailer.png`}
                alt="9x6 bubble mailer"
                className={styles.mailer_img}
              />
              <span className={styles.mailer_dim}>9 x 6 in.</span>
            </div>
          </div>
          <p className={styles.packing_text}>
            Place floss heads in 9x6 bubble mailer.
          </p>
        </div>

        <button
          className={`${styles.download_btn} ${isFormComplete && !isDownloading ? styles.download_btn_active : ""}`}
          disabled={!isFormComplete || isDownloading}
          onClick={handleDownload}
        >
          {isDownloading ? downloadText : "Download shipping label"}
        </button>
      </div>
    </div>
  );
};

export default FlausFlosserForm;
